import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Search, Building2, ArrowRight, ChevronDown, ChevronUp, Mail, X, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import ClientAccountForm from "@/components/ClientAccountForm";
import { stagedName } from "@/lib/stagedFiles";

const EMPTY_CLIENT = {
  name: "",
  contact_name: "", contact_phone: "",
  email: "", phone: "",
  accounts_email: "", accounts_phone: "",
  additional_emails: [],
  po_required_for_invoice: false,
  address_line1: "", address_line2: "", city: "", postcode: "",
  country: "United Kingdom", is_active: true, notes: "", description: "",
  bank_name: "", bank_account_number: "", bank_sort_code: "",
  services: [], vehicles_required: [],
  service_collection_addresses: {},
};

// Keep in sync with SERVICE_OPTIONS in JobForm.jsx.
const CLIENT_SERVICE_OPTIONS = [
  { id: "Same-day",   label: "Same-day",   hint: "Time-critical, delivered same day" },
  { id: "Next-day",   label: "Next-day",   hint: "Booked today, delivered tomorrow" },
  { id: "Multi-drop", label: "Multi-drop", hint: "Multiple stops on one run" },
  { id: "Overnight",  label: "Overnight",  hint: "Delivered by early morning" },
  { id: "Scheduled",  label: "Scheduled",  hint: "Booked in advance for a set date" },
  { id: "DSP",        label: "DSP",        hint: "Ongoing driver-supply contract" },
];

// Preset vehicle options — client can pick multiple, and the free-text field
// below lets ops add anything bespoke ("Tail-lift 3.5t with pump truck", etc).
const CLIENT_VEHICLE_OPTIONS = [
  "Small van", "SWB van", "LWB van", "Luton",
  "3.5t", "7.5t", "18t", "Artic",
];

export default function Clients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Restricted mode: user has `clients-add-only` but NOT full `clients` — they
  // can add a new client but the existing list is hidden from them.
  const canSeeList = hasPermission(user, "clients");
  const [clients, setClients] = useState([]);
  const [siteCounts, setSiteCounts] = useState({});
  const [siteNames, setSiteNames] = useState({}); // client_id -> concatenated site names (lowercase)
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CLIENT);
  const [search, setSearch] = useState("");
  const [showExtras, setShowExtras] = useState(false);
  const [stagedForms, setStagedForms] = useState([]);

  const load = async () => {
    // Add-only staff must not see the list of existing clients.
    if (!canSeeList) { setClients([]); setSiteCounts({}); setSiteNames({}); return; }
    const r = await api.get("/clients");
    setClients(r.data);
    const counts = {};
    const names = {};
    await Promise.all(
      r.data.map(async (c) => {
        const s = await api.get(`/clients/${c.id}/sites`);
        counts[c.id] = s.data.length;
        names[c.id] = s.data.map((x) => x.name || "").join(" ").toLowerCase();
      }),
    );
    setSiteCounts(counts);
    setSiteNames(names);
  };
  useEffect(() => { load(); }, [canSeeList]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_CLIENT); setStagedForms([]); setShowExtras(false); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY_CLIENT, ...c, additional_emails: c.additional_emails || [] }); setShowExtras((c.additional_emails || []).length > 0); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required.");
    const payload = {
      ...form,
      additional_emails: (form.additional_emails || []).filter((r) => (r.email || "").trim() || (r.phone || "").trim()),
    };
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, payload);
        toast.success("Client updated.");
        setOpen(false);
        load();
      } else {
        const r = await api.post("/clients", payload);
        let failed = 0;
        for (const f of stagedForms) {
          const fd = new FormData();
          fd.append("file", f.file, stagedName(f));
          try { await api.post(`/uploads?entity_type=client&entity_id=${r.data.id}__account_form`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
          catch { failed += 1; }
        }
        if (failed) toast.warning(`${failed} account form file${failed === 1 ? "" : "s"} failed to upload — attach again via Edit`);
        setStagedForms([]);
        setOpen(false);
        if (canSeeList) {
          toast.success(`${r.data.name} created. Add sites next.`);
          navigate(`/clients/${r.data.id}?addSite=1`);
        } else {
          // Restricted users stay on this page — surface the auto-assigned
          // customer number in the toast so they can pass it back to their manager.
          toast.success(`${r.data.name} created — customer number ${r.data.number}.`);
          setForm(EMPTY_CLIENT);
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This will also remove all sites and subcontractor assignments for this client.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Client deleted.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = clients.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    if (q === "active") return c.is_active !== false;
    if (q === "inactive" || q === "unactive") return c.is_active === false;
    const hay = `${c.number || ""} ${c.name || ""} ${c.contact_name || ""} ${c.contact_phone || ""} ${c.email || ""} ${c.accounts_email || ""} ${c.phone || ""} ${c.accounts_phone || ""} ${c.city || ""} ${c.postcode || ""} ${c.bank_name || ""} ${c.bank_account_number || ""} ${c.bank_sort_code || ""} ${(c.services || []).join(" ")} ${siteNames[c.id] || ""}`.toLowerCase();
    return hay.includes(q);
  });

  return (
    <div data-testid="clients-page" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Directory</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Clients</h1>
          <p className="text-sm text-slate-600 mt-1">
            {canSeeList
              ? "Companies you deliver for. Each client can have multiple sites and assigned subcontractors."
              : "Add a new client below. Existing clients are hidden from your account."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn" onClick={openCreate} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-2" /> New Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Client" : "New Client"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              {/* Customer number — auto-assigned by the server, shown read-only when editing */}
              <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3 bg-orange-50/60" data-testid="cli-number-block">
                <div>
                  <Label className="text-sm font-medium text-slate-900">Customer number</Label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {editing
                      ? "Assigned automatically when the client was added — used across POs, self-bills and reports."
                      : "We'll assign the next available D4PS-#### number as soon as you save."}
                  </p>
                </div>
                <div className="font-mono text-sm font-semibold text-slate-900 tabular-nums" data-testid="cli-number-value">
                  {editing?.number || (editing ? "—" : "Auto")}
                </div>
              </div>
              <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3 bg-slate-50">
                <div>
                  <Label className="text-sm font-medium text-slate-900">Active</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Inactive clients are hidden from job pickers but kept in history.</p>
                </div>
                <Switch
                  data-testid="cli-active-toggle"
                  checked={form.is_active !== false}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3 bg-slate-50">
                <div>
                  <Label className="text-sm font-medium text-slate-900">PO required for invoice</Label>
                  <p className="text-xs text-slate-500 mt-0.5">If on, this client requires a Purchase Order number before you can invoice them.</p>
                </div>
                <Switch
                  data-testid="cli-po-required-toggle"
                  checked={!!form.po_required_for_invoice}
                  onCheckedChange={(v) => setForm({ ...form, po_required_for_invoice: v })}
                />
              </div>
              <FieldI label="Client name" testid="cli-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required className="md:col-span-2" />
              <FieldI label="Contact person name" testid="cli-contact-name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
              <FieldI label="Contact person number" testid="cli-contact-phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
              <FieldI label="Main email" testid="cli-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <FieldI label="Main phone" testid="cli-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <FieldI label="Accounts email" testid="cli-accounts-email" type="email" value={form.accounts_email} onChange={(v) => setForm({ ...form, accounts_email: v })} placeholder="accounts@..." />
              <FieldI label="Accounts phone" testid="cli-accounts-phone" value={form.accounts_phone} onChange={(v) => setForm({ ...form, accounts_phone: v })} />

              {/* Other contacts — collapsible (emails & numbers) */}
              <div className="md:col-span-2">
                <button
                  type="button"
                  data-testid="cli-toggle-extras"
                  onClick={() => setShowExtras((v) => !v)}
                  className="w-full flex items-center justify-between rounded-sm border border-slate-200 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-2">
                    <Mail className="h-4 w-4 text-slate-500" />
                    Other contacts
                    {form.additional_emails.length > 0 && (
                      <span className="text-xs text-slate-500">({form.additional_emails.length})</span>
                    )}
                  </span>
                  {showExtras ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                </button>
                {showExtras && (
                  <div className="mt-3 space-y-2" data-testid="cli-extras-panel">
                    {form.additional_emails.length === 0 && (
                      <div className="text-xs text-slate-500 px-1">No extra contacts yet. Add as many as you need — e.g. operations, scheduling, depot.</div>
                    )}
                    {form.additional_emails.map((row, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <Input
                          data-testid={`cli-extra-label-${idx}`}
                          placeholder="Label (e.g. Operations)"
                          value={row.label || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => {
                              const next = [...f.additional_emails];
                              next[idx] = { ...next[idx], label: v };
                              return { ...f, additional_emails: next };
                            });
                          }}
                          className="col-span-3 rounded-sm border-slate-300 h-9"
                        />
                        <Input
                          data-testid={`cli-extra-email-${idx}`}
                          type="email"
                          placeholder="email@company.com"
                          value={row.email || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => {
                              const next = [...f.additional_emails];
                              next[idx] = { ...next[idx], email: v };
                              return { ...f, additional_emails: next };
                            });
                          }}
                          className="col-span-5 rounded-sm border-slate-300 h-9"
                        />
                        <Input
                          data-testid={`cli-extra-phone-${idx}`}
                          placeholder="Phone"
                          value={row.phone || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => {
                              const next = [...f.additional_emails];
                              next[idx] = { ...next[idx], phone: v };
                              return { ...f, additional_emails: next };
                            });
                          }}
                          className="col-span-3 rounded-sm border-slate-300 h-9"
                        />
                        <button
                          type="button"
                          data-testid={`cli-extra-remove-${idx}`}
                          onClick={() => setForm((f) => ({ ...f, additional_emails: f.additional_emails.filter((_, i) => i !== idx) }))}
                          className="col-span-1 inline-flex items-center justify-center text-slate-400 hover:text-red-600"
                          title="Remove"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="cli-extra-add"
                      className="rounded-sm"
                      onClick={() => setForm((f) => ({ ...f, additional_emails: [...f.additional_emails, { label: "", email: "", phone: "" }] }))}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add contact
                    </Button>
                  </div>
                )}
              </div>

              <FieldI label="Address line 1" testid="cli-addr1" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} className="md:col-span-2" />
              <FieldI label="Address line 2" testid="cli-addr2" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} className="md:col-span-2" />
              <FieldI label="City" testid="cli-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <FieldI label="Postcode" testid="cli-postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} />

              {/* Payable-to bank details — used on POs / self-bills / refunds */}
              <div className="md:col-span-2 rounded-sm border border-slate-200 bg-slate-50/70 p-4" data-testid="cli-payable-block">
                <div className="flex items-center gap-2 mb-3">
                  <Banknote className="h-4 w-4 text-orange-500" />
                  <div>
                    <Label className="text-sm font-medium text-slate-900">Payable to</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Client&apos;s bank details — where any payments to this client are sent.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <FieldI label="Bank name" testid="cli-bank-name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} placeholder="e.g. Barclays" />
                  <FieldI
                    label="Account number"
                    testid="cli-bank-account"
                    value={form.bank_account_number}
                    onChange={(v) => setForm({ ...form, bank_account_number: v.replace(/\D/g, "").slice(0, 8) })}
                    placeholder="8 digits"
                    inputMode="numeric"
                  />
                  <FieldI
                    label="Sort code"
                    testid="cli-bank-sort"
                    value={form.bank_sort_code}
                    onChange={(v) => setForm({ ...form, bank_sort_code: formatSortCode(v) })}
                    placeholder="12-34-56"
                    inputMode="numeric"
                  />
                </div>
              </div>

              {/* Services this client uses — quick tick list */}
              <div className="md:col-span-2 rounded-sm border border-slate-200 bg-slate-50/70 p-4" data-testid="cli-services-block">
                <div className="mb-3">
                  <Label className="text-sm font-medium text-slate-900">Services used</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Tick every service this client books with you. Shown next to their name on the Clients list.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {CLIENT_SERVICE_OPTIONS.map((s) => {
                    const on = (form.services || []).includes(s.id);
                    const addr = (form.service_collection_addresses || {})[s.id] || "";
                    return (
                      <div
                        key={s.id}
                        className={`rounded-sm border transition ${on ? "border-orange-400 bg-orange-50/60" : "border-slate-200 bg-white"}`}
                      >
                        <label
                          data-testid={`cli-service-${s.id}`}
                          className="flex items-start gap-2 px-2.5 py-1.5 text-xs cursor-pointer select-none"
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => {
                              const list = form.services || [];
                              const nextSvcs = on ? list.filter((x) => x !== s.id) : [...list, s.id];
                              // Drop the address when the service is unticked so the record stays clean.
                              const nextAddrs = { ...(form.service_collection_addresses || {}) };
                              if (on) delete nextAddrs[s.id];
                              setForm({ ...form, services: nextSvcs, service_collection_addresses: nextAddrs });
                            }}
                            className="mt-0.5 h-3.5 w-3.5 rounded-sm accent-orange-500"
                          />
                          <span className="min-w-0 flex-1">
                            <span className={`block font-medium ${on ? "text-slate-900" : "text-slate-700"}`}>{s.label}</span>
                            <span className="block text-[10.5px] text-slate-500 leading-snug">{s.hint}</span>
                          </span>
                        </label>
                        {on && (
                          <div className="border-t border-orange-200 bg-white/70 px-2.5 py-2">
                            <Label className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Default collection address</Label>
                            <Textarea
                              data-testid={`cli-service-addr-${s.id}`}
                              rows={2}
                              value={addr}
                              onChange={(e) => setForm({
                                ...form,
                                service_collection_addresses: { ...(form.service_collection_addresses || {}), [s.id]: e.target.value },
                              })}
                              placeholder="e.g. Warehouse A, Manchester M1 1AA"
                              className="mt-1 rounded-sm border-slate-300 text-xs"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Vehicle types the client wants — presets + free-text extras */}
              <div className="md:col-span-2 rounded-sm border border-slate-200 bg-slate-50/70 p-4" data-testid="cli-vehicles-block">
                <div className="mb-3">
                  <Label className="text-sm font-medium text-slate-900">Vehicle required</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Tick the vehicles this client typically uses, or add a bespoke one below.</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {CLIENT_VEHICLE_OPTIONS.map((v) => {
                    const on = (form.vehicles_required || []).includes(v);
                    return (
                      <label
                        key={v}
                        data-testid={`cli-vehicle-${v.replace(/\s+/g, "-")}`}
                        className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-sm border text-xs cursor-pointer select-none transition ${on ? "border-orange-400 bg-orange-50/60 text-slate-900" : "border-slate-200 bg-white text-slate-700"}`}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => {
                            const list = form.vehicles_required || [];
                            const next = on ? list.filter((x) => x !== v) : [...list, v];
                            setForm({ ...form, vehicles_required: next });
                          }}
                          className="h-3.5 w-3.5 rounded-sm accent-orange-500"
                        />
                        <span className="font-medium">{v}</span>
                      </label>
                    );
                  })}
                </div>
                {/* Extras — free-form chips the operator can type in */}
                <div className="mt-3">
                  <Label className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Other / bespoke</Label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {(form.vehicles_required || [])
                      .filter((v) => !CLIENT_VEHICLE_OPTIONS.includes(v))
                      .map((v) => (
                        <span key={v} data-testid={`cli-vehicle-chip-${v}`}
                              className="inline-flex items-center gap-1 rounded-sm bg-slate-900 text-white text-[11px] px-2 py-0.5">
                          {v}
                          <button
                            type="button"
                            onClick={() => setForm({
                              ...form,
                              vehicles_required: (form.vehicles_required || []).filter((x) => x !== v),
                            })}
                            className="hover:text-rose-300"
                            aria-label={`Remove ${v}`}
                          >×</button>
                        </span>
                      ))}
                  </div>
                  <Input
                    data-testid="cli-vehicle-custom"
                    placeholder="Type a bespoke vehicle and press Enter (e.g. Tail-lift 3.5t with pump truck)"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.currentTarget.value.trim()) {
                        e.preventDefault();
                        const val = e.currentTarget.value.trim();
                        if (!(form.vehicles_required || []).includes(val)) {
                          setForm({ ...form, vehicles_required: [...(form.vehicles_required || []), val] });
                        }
                        e.currentTarget.value = "";
                      }
                    }}
                    className="mt-1 rounded-sm border-slate-300 text-sm"
                  />
                </div>
              </div>

              <FieldI label="Notes" testid="cli-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} className="md:col-span-2" />

              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Description</label>
                <textarea
                  data-testid="cli-description"
                  value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  placeholder="What does the client do? Typical loads, vehicle types, service quirks, key contacts on site…"
                  className="mt-1 w-full rounded-sm border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                />
                <p className="text-[11px] text-slate-500 mt-1">Shown on the client profile PDF, below Payable to.</p>
              </div>
              <ClientAccountForm clientId={editing?.id} staged={stagedForms} setStaged={setStagedForms} />
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="cli-save-btn" onClick={save} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {canSeeList ? (
        <Card className="rounded-sm border border-slate-200 shadow-none">
          <div className="p-4 border-b border-slate-100">
            <div className="relative max-w-md">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="clients-search" placeholder='Search by customer #, name, email, city, site name, "active" or "inactive"...' value={search}
                onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm border-slate-300" />
            </div>
          </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 uppercase text-xs tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Customer #</th>
              <th className="text-left px-5 py-3">Client</th>
              <th className="text-left px-5 py-3">Services</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Phone</th>
              <th className="text-left px-5 py-3">City</th>
              <th className="text-left px-5 py-3">Payable to</th>
              <th className="text-right px-5 py-3">Sites</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center text-slate-500 py-12">
                  <Building2 className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  <div className="font-medium text-slate-700">No clients yet</div>
                  <div className="text-xs">Click &quot;New Client&quot; to add your first one.</div>
                </td>
              </tr>
            ) : (
              filtered.map((c) => (
                <tr key={c.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${c.is_active === false ? "opacity-70" : ""}`} data-testid={`client-row-${c.id}`}>
                  <td className="px-5 py-3 font-mono text-xs text-slate-700 tabular-nums" data-testid={`client-number-${c.id}`}>{c.number || "—"}</td>
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/clients/${c.id}`} className="text-slate-900 hover:underline inline-flex items-center gap-2">
                      {c.name} <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    {c.is_active === false ? (
                      <Badge className="ml-2 rounded-sm bg-amber-50 text-amber-700 hover:bg-amber-50 font-normal border border-amber-200">Inactive</Badge>
                    ) : (
                      <Badge className="ml-2 rounded-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-50 font-normal border border-emerald-200">Active</Badge>
                    )}
                  </td>
                  <td className="px-5 py-3" data-testid={`client-services-${c.id}`}>
                    {(c.services || []).length ? (
                      <div className="flex flex-wrap gap-1">
                        {(c.services || []).map((s) => (
                          <span key={s} className="inline-flex items-center rounded-sm bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 text-[10px] font-medium">{s}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{c.email || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.phone || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{c.city || "—"}</td>
                  <td className="px-5 py-3 text-slate-600" data-testid={`client-payable-${c.id}`}>
                    {c.bank_name ? (
                      <div className="min-w-0">
                        <div className="font-medium text-slate-800 truncate">{c.bank_name}</div>
                        {(c.bank_account_number || c.bank_sort_code) && (
                          <div className="text-[11px] text-slate-500 tabular-nums font-mono">
                            {c.bank_sort_code || "—"}{c.bank_account_number ? ` · ${c.bank_account_number}` : ""}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    <Link
                      to={`/clients/${c.id}`}
                      data-testid={`manage-sites-${c.id}`}
                      className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 hover:underline"
                    >
                      {siteCounts[c.id] ?? 0} {(siteCounts[c.id] ?? 0) === 1 ? "site" : "sites"}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => openEdit(c)} data-testid={`cli-edit-${c.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 text-red-600 hover:text-red-700" onClick={() => remove(c)} data-testid={`cli-del-${c.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </Card>
      ) : (
        <Card data-testid="clients-restricted-card" className="rounded-sm border border-orange-200 bg-orange-50/40 shadow-none p-8 text-center">
          <Building2 className="h-10 w-10 mx-auto mb-3 text-orange-400" />
          <div className="font-medium text-slate-900">Restricted view</div>
          <div className="text-xs text-slate-600 mt-1 max-w-md mx-auto">
            Your account can add new clients but not view the existing directory. Click &quot;New Client&quot; above to add one, then hand the client&#39;s customer number back to your manager.
          </div>
        </Card>
      )}
    </div>
  );
}

function FieldI({ label, value, onChange, testid, type = "text", className = "", required, ...rest }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm border-slate-300" {...rest} />
    </div>
  );
}

// UK sort code: keep 6 digits, format as "12-34-56" while typing.
function formatSortCode(v) {
  const digits = (v || "").replace(/\D/g, "").slice(0, 6);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)].filter(Boolean);
  return parts.join("-");
}
